if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.simplifyCollectionModal(productElement);
      }

      // Velune Closing Sale: keep the compact buying flow, but restore the
      // original stock/review proof, gallery badges, dispatch pill and shipping.
      // Runs for initial content and for section re-renders after variant changes.
      simplifyCollectionModal(productElement) {
        if (!this.classList.contains('velune-collection-quick-add')) return;

        const infoContainer = productElement?.querySelector('.product__info-container');
        if (!infoContainer) return;

        // Keep the original free-shipping sentence without the larger gift card.
        const shippingSource = infoContainer.querySelector('.velune-wallet-warranty .vww-shipping');
        const shippingLine = shippingSource?.cloneNode(true);
        if (shippingLine) {
          shippingLine.classList.add('velune-quickadd-shipping');
        }

        const essentialBlocks = [
          '.velune-stock-simple',
          '.velune-review-proof',
          '.product__title',
          '[id^="price-"]',
          'variant-selects',
          '[id^="Quantity-Form-"]',
          '.velune-buy-buttons-zero-gap',
          '.product__view-details',
        ].join(', ');

        const keep = new Set();
        Array.from(infoContainer.children).forEach((block) => {
          if (!block.matches(essentialBlocks)) return;

          keep.add(block);
          // The stock and review components each ship their own CSS immediately
          // after their markup; retain only those two original style blocks.
          if (block.matches('.velune-stock-simple, .velune-review-proof')) {
            const style = block.nextElementSibling;
            if (style?.tagName === 'STYLE') keep.add(style);
          }
        });

        Array.from(infoContainer.children).forEach((block) => {
          if (!keep.has(block)) block.remove();
        });

        const detailsLink = infoContainer.querySelector('.product__view-details');
        if (shippingLine) {
          if (detailsLink) detailsLink.before(shippingLine);
          else infoContainer.append(shippingLine);
        }

        // Reviews in a quick-add modal must link to the actual PDP reviews,
        // rather than try to scroll the collection page behind the dialog.
        const reviewLink = infoContainer.querySelector('.velune-review-proof');
        if (reviewLink && productElement.dataset.url) {
          reviewLink.setAttribute('href', productElement.dataset.url.split('#')[0] + '#bkreviews');
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
